/**
 * DDD Service Factory - No Inversify Pattern
 * 
 * Simple, type-safe factory pattern following party-manager approach.
 * Manages all DDD domain services without dependency injection complexity.
 */

import { MongoClient, Db } from 'mongodb';
// @ts-ignore - @swoft/persistence doesn't have TypeScript declarations yet
import { getMongoDb } from '@swoft/persistence';

// PROPER Application Services (CQRS preserved, Inversify removed)
import { DomainApplicationService } from '../../application/services/DomainApplicationService';
import { BoundedContextApplicationService } from '../../application/services/BoundedContextApplicationService';

// CQRS Handlers (fixed to remove Inversify)
import { CreateDomainHandler } from '../../bounded-contexts/strategic-design/application/handlers/CreateDomainHandler';
import { UpdateDomainHandler } from '../../bounded-contexts/strategic-design/application/handlers/UpdateDomainHandler';
import { GetDomainHandler } from '../../bounded-contexts/strategic-design/application/handlers/GetDomainHandler';
import { ListDomainsHandler } from '../../bounded-contexts/strategic-design/application/handlers/ListDomainsHandler';

// Bounded Context Handlers
import { CreateBoundedContextHandler } from '../../bounded-contexts/strategic-design/application/handlers/CreateBoundedContextHandler';
import { GetBoundedContextHandler } from '../../bounded-contexts/strategic-design/application/handlers/GetBoundedContextHandler';
import { ListBoundedContextsHandler } from '../../bounded-contexts/strategic-design/application/handlers/ListBoundedContextsHandler';

// Command/Query Services  
import { DomainCommandService } from '../../bounded-contexts/strategic-design/application/services/DomainCommandService';
import { DomainCQRSQueryService } from '../../bounded-contexts/strategic-design/application/services/DomainQueryService';

// Repositories (fixed to remove Inversify)
import { MongoDomainRepository } from '../../bounded-contexts/meta-model-design/infrastructure/repositories/MongoDomainRepository';
import { MongoBoundedContextRepository } from '../../bounded-contexts/meta-model-design/infrastructure/repositories/MongoBoundedContextRepository';

export interface DDDHealthStatus {
  healthy: boolean;
  database: string;
  uptime: number;
  timestamp: string;
  memory: NodeJS.MemoryUsage;
}

/**
 * Service factory that manages all DDD dependencies
 * No Inversify, no complexity, just simple factory pattern
 */
export class ServiceFactory {
  private static instance: ServiceFactory;
  
  private database?: Db;
  
  // Repository instances (lazy loaded)
  private domainRepo?: MongoDomainRepository;
  private boundedContextRepo?: MongoBoundedContextRepository;
  
  // Command/Query Service instances (lazy loaded)
  private domainCommandService?: DomainCommandService;
  private domainQueryService?: DomainCQRSQueryService;
  private boundedContextCommandService?: any; // BoundedContextCommandService
  private boundedContextQueryService?: any; // BoundedContextQueryService
  
  // Handler instances (lazy loaded)
  private createDomainHandler?: CreateDomainHandler;
  private updateDomainHandler?: UpdateDomainHandler;
  private getDomainHandler?: GetDomainHandler;
  private listDomainsHandler?: ListDomainsHandler;
  
  // Bounded Context Handler instances (lazy loaded)
  private createBoundedContextHandler?: CreateBoundedContextHandler;
  private getBoundedContextHandler?: GetBoundedContextHandler;
  private listBoundedContextsHandler?: ListBoundedContextsHandler;
  
  // Application Service instances (lazy loaded)
  private domainAppService?: DomainApplicationService;
  private boundedContextAppService?: BoundedContextApplicationService;

  private constructor() {}

  static getInstance(): ServiceFactory {
    if (!ServiceFactory.instance) {
      ServiceFactory.instance = new ServiceFactory();
    }
    return ServiceFactory.instance;
  }

  /**
   * Initialize database connection
   * Should be called once at application startup
   */
  async initialize(): Promise<void> {
    try {
      console.log('🔧 Initializing DDD Service Factory...');
      
      // Use the existing @swoft/persistence MongoDB connection
      this.database = getMongoDb();
      
      // Verify connection works
      if (!this.database) {
        throw new Error('Failed to initialize database connection');
      }
      await this.database.admin().ping();
      
      console.log('✅ DDD Service Factory initialized successfully');
    } catch (error) {
      console.error('❌ Failed to initialize DDD Service Factory:', error);
      throw error;
    }
  }

  /**
   * Gracefully shutdown all connections
   */
  async shutdown(): Promise<void> {
    try {
      console.log('🛑 Shutting down DDD Service Factory...');
      // MongoDB connection managed by @swoft/persistence, no need to close here
      console.log('✅ DDD Service Factory shut down successfully');
    } catch (error) {
      console.error('❌ Error during DDD Service Factory shutdown:', error);
      throw error;
    }
  }

  /**
   * Get database instance
   */
  private getDatabase(): Db {
    if (!this.database) {
      throw new Error('DDD Service Factory not initialized. Call initialize() first.');
    }
    return this.database;
  }

  // ========================================================================
  // Repository Getters (Lazy Loading)
  // ========================================================================

  private getDomainRepository(): MongoDomainRepository {
    if (!this.domainRepo) {
      this.domainRepo = new MongoDomainRepository(this.getDatabase());
    }
    return this.domainRepo;
  }

  private getBoundedContextRepository(): MongoBoundedContextRepository {
    if (!this.boundedContextRepo) {
      this.boundedContextRepo = new MongoBoundedContextRepository(this.getDatabase());
    }
    return this.boundedContextRepo;
  }

  // ========================================================================
  // Command/Query Service Getters (Lazy Loading)
  // ========================================================================

  private getDomainCommandService(): DomainCommandService {
    if (!this.domainCommandService) {
      // Simple EventBus implementation for MCP server
      const eventBus = {
        publish: async (event: any) => {
          console.log(`📢 Domain Event Published: ${event.constructor?.name || 'Event'}`, {
            eventId: event.id,
            aggregateId: event.aggregateId,
            timestamp: new Date().toISOString()
          });
          // For now, just log events - no subscribers needed for MCP server
        }
      };
      this.domainCommandService = new DomainCommandService(
        this.getDomainRepository(),
        eventBus as any
      );
    }
    return this.domainCommandService;
  }

  private getDomainQueryService(): DomainCQRSQueryService {
    if (!this.domainQueryService) {
      this.domainQueryService = new DomainCQRSQueryService(
        this.getDomainRepository(),
        this.getBoundedContextRepository()
      );
    }
    return this.domainQueryService;
  }

  private getBoundedContextCommandService(): any {
    if (!this.boundedContextCommandService) {
      // Import the service dynamically to avoid circular dependencies
      const { BoundedContextCommandService } = require('../../bounded-contexts/strategic-design/application/services/BoundedContextCommandService');
      this.boundedContextCommandService = new BoundedContextCommandService(
        this.getBoundedContextRepository(),
        this.getDomainRepository(),
        this.getEventBus()
      );
    }
    return this.boundedContextCommandService;
  }

  private getBoundedContextQueryService(): any {
    if (!this.boundedContextQueryService) {
      // Import the service dynamically to avoid circular dependencies
      const { BoundedContextQueryService } = require('../../bounded-contexts/strategic-design/application/services/BoundedContextQueryService');
      this.boundedContextQueryService = new BoundedContextQueryService(
        this.getBoundedContextRepository(),
        this.getDomainRepository()
      );
    }
    return this.boundedContextQueryService;
  }

  private getEventBus(): any {
    // Simple EventBus implementation for MCP server
    return {
      publish: async (event: any) => {
        console.log(`📢 Event Published: ${event.constructor?.name || 'Event'}`, {
          eventId: event.id,
          aggregateId: event.aggregateId,
          timestamp: new Date().toISOString()
        });
        // For now, just log events - no subscribers needed for MCP server
      }
    };
  }

  // ========================================================================
  // Handler Getters (Lazy Loading) 
  // ========================================================================

  private getCreateDomainHandler(): CreateDomainHandler {
    if (!this.createDomainHandler) {
      this.createDomainHandler = new CreateDomainHandler(this.getDomainCommandService());
    }
    return this.createDomainHandler;
  }

  private getUpdateDomainHandler(): UpdateDomainHandler {
    if (!this.updateDomainHandler) {
      this.updateDomainHandler = new UpdateDomainHandler(this.getDomainCommandService());
    }
    return this.updateDomainHandler;
  }

  private getGetDomainHandler(): GetDomainHandler {
    if (!this.getDomainHandler) {
      this.getDomainHandler = new GetDomainHandler(this.getDomainQueryService());
    }
    return this.getDomainHandler;
  }

  private getListDomainsHandler(): ListDomainsHandler {
    if (!this.listDomainsHandler) {
      this.listDomainsHandler = new ListDomainsHandler(this.getDomainQueryService());
    }
    return this.listDomainsHandler;
  }

  // ========================================================================
  // Bounded Context Handler Getters (Lazy Loading)
  // ========================================================================

  private getCreateBoundedContextHandler(): CreateBoundedContextHandler {
    if (!this.createBoundedContextHandler) {
      // Import the handler dynamically to avoid circular dependencies
      const { CreateBoundedContextHandler } = require('../../bounded-contexts/strategic-design/application/handlers/CreateBoundedContextHandler');
      this.createBoundedContextHandler = new CreateBoundedContextHandler(
        this.getBoundedContextCommandService(),
        this.getEventBus()
      );
    }
    return this.createBoundedContextHandler!; // Non-null assertion since we just created it
  }

  private getGetBoundedContextHandler(): GetBoundedContextHandler {
    if (!this.getBoundedContextHandler) {
      // Import the handler dynamically to avoid circular dependencies
      const { GetBoundedContextHandler } = require('../../bounded-contexts/strategic-design/application/handlers/GetBoundedContextHandler');
      this.getBoundedContextHandler = new GetBoundedContextHandler(
        this.getBoundedContextQueryService()
      );
    }
    return this.getBoundedContextHandler!; // Non-null assertion since we just created it
  }

  private getListBoundedContextsHandler(): ListBoundedContextsHandler {
    if (!this.listBoundedContextsHandler) {
      // Import the handler dynamically to avoid circular dependencies
      const { ListBoundedContextsHandler } = require('../../bounded-contexts/strategic-design/application/handlers/ListBoundedContextsHandler');
      this.listBoundedContextsHandler = new ListBoundedContextsHandler(
        this.getBoundedContextQueryService()
      );
    }
    return this.listBoundedContextsHandler!; // Non-null assertion since we just created it
  }

  // ========================================================================
  // Application Service Getters (Public API for MCP)
  // ========================================================================

  getDomainApplicationService(): DomainApplicationService {
    if (!this.domainAppService) {
      this.domainAppService = new DomainApplicationService(
        this.getCreateDomainHandler(),
        this.getUpdateDomainHandler(),
        this.getGetDomainHandler(),
        this.getListDomainsHandler()
      );
    }
    return this.domainAppService;
  }

  getBoundedContextApplicationService(): BoundedContextApplicationService {
    if (!this.boundedContextAppService) {
      // Use the actual handler instances by calling the getters directly
      const createHandler = this.getCreateBoundedContextHandler();
      const getHandler = this.getGetBoundedContextHandler();  
      const listHandler = this.getListBoundedContextsHandler();
      
      this.boundedContextAppService = new BoundedContextApplicationService(
        createHandler,
        getHandler,
        listHandler
      );
    }
    return this.boundedContextAppService;
  }

  /**
   * Get health status
   */
  async getHealthStatus(): Promise<DDDHealthStatus> {
    const status: DDDHealthStatus = {
      healthy: false,
      database: 'disconnected',
      uptime: process.uptime(),
      memory: process.memoryUsage(),
      timestamp: new Date().toISOString()
    };

    try {
      if (this.database) {
        await this.database.admin().ping();
        status.database = 'connected';
        status.healthy = true;
      }
    } catch (error) {
      console.error('❌ DDD health check failed:', error);
    }

    return status;
  }
}